/**
 * Section rendering constants and parser.
 *
 * Centralizes the section-id strings shared between `cart-fetch` (which
 * includes them in the `sections` POST payload) and `cart-items` /
 * `cart-icon-bubble` (which pluck the rendered HTML out of the response).
 * A single source of truth means a rename touches one file; preflight pins
 * the literals here and trusts that consumers imported them.
 *
 * `parseSection` wraps `parseFragment` with a console-warn when the
 * response HTML exists but the target element is missing — a silent no-op
 * from `parseFragment` alone was hard to diagnose when section IDs drifted.
 *
 * Source path: theme/js/core/sections.esm.js
 * Built name : ./sections.js
 *
 * AI: Entirely written by AI
 * Date: 2026-05-07
 * Model: Claude Sonnet 4.6
 *
 * [Sections]
 */

import { log } from './log.js';
import { parseFragment } from './dom.js';

export const SECTION_CART_ITEMS = 'cart-items';
export const SECTION_CART_ICON_BUBBLE = 'cart-icon-bubble';

export function parseSection(html, sectionId) {
  const el = parseFragment(html, sectionId);
  if (html && !el) {
    log('[Sections] parseSection: no <' + sectionId + '> found in response HTML');
  }
  return el;
}

/**
 * Fetch a single section's rendered HTML via Shopify's Section Rendering API.
 *
 * GET `${url}?sections=${sectionId}` returns `{ [sectionId]: "<html>" }`.
 * Used by modal consumers (sale-policy, newsletter, hemming, sizing) to
 * pull substantial markup on-demand instead of shipping it inline as a
 * `<template>` on every page load. Callers manage their own caching;
 * this helper is intentionally cache-free so error retries and per-key
 * eviction stay in the caller's domain.
 *
 * Throws on:
 *   - non-2xx response (network / server error)
 *   - non-JSON Content-Type (e.g. storefront password page redirect at
 *     HTTP 200, Cloudflare challenge, maintenance page)
 *   - JSON without the requested section id (e.g. section not registered
 *     for the URL's resolved template) — without this guard the helper
 *     would return undefined and per-consumer promise caches would store
 *     the resolved-undefined value permanently, silently breaking every
 *     subsequent open until full page reload.
 */
// Resolve the preview theme id to propagate on section fetches.
// Shopify's preview-theme session cookie maintains preview context for
// HTML navigations but does NOT apply to `?sections=` API requests, so
// without explicit propagation the fetch silently hits the published
// theme (which may not have the requested section file yet → `null` in
// the response body → cache-poisoned promise → "missing from response"
// throw on the next click).
//
// Two signals, checked in order:
//   1. `preview_theme_id` in the page URL — most explicit; survives only
//      until the customer clicks a relative link that strips it.
//   2. `window.ep_appinfo.theme` hydrated by content-for-footer.liquid
//      from Shopify Liquid's `theme` global. `role === 'main'` is the
//      live theme (no propagation); anything else is a preview /
//      development theme and `theme.id` is the value we need.
//
// In production against the live theme, both checks return null and the
// fetch URL is unchanged.
function getPreviewThemeId() {
  const m = window.location.search.match(/[?&]preview_theme_id=(\d+)/);
  if (m) return m[1];
  const info = window.ep_appinfo;
  if (info && info.theme && info.theme.id && info.theme.role && info.theme.role !== 'main') {
    return String(info.theme.id);
  }
  return null;
}

export async function fetchSection(sectionId, url) {
  const base = url || '/';
  const sep = base.indexOf('?') >= 0 ? '&' : '?';
  const previewId = getPreviewThemeId();
  const previewParam = previewId ? '&preview_theme_id=' + previewId : '';
  const requestUrl = base + sep + 'sections=' + encodeURIComponent(sectionId) + previewParam;
  log('[Sections] fetching ' + sectionId + ' from ' + requestUrl);
  // Do NOT set `Accept: application/json`. Shopify's Section Rendering
  // API returns JSON when `?sections=` is in the URL regardless of
  // Accept, but it specifically OMITS the section keys from the body
  // when Accept is `application/json` — verified empirically by
  // `scripts/diagnose-section-fetch.js`. `*/*` and `text/html` both
  // return the section HTML; `*/*` is the fetch default. Cart-fetch
  // sets the JSON Accept header without issue because `/cart/*.js` is
  // a different endpoint family.
  const response = await fetch(requestUrl, {
    credentials: 'same-origin',
  });
  if (!response.ok) {
    // `shopify theme dev` localhost proxy trap: the Section Rendering API
    // returns 401 with `WWW-Authenticate: Bearer realm="…"` because the
    // proxy doesn't inject the Bearer token JS fetches need (cookie auth
    // alone is not enough for ?sections=… on the index/product templates
    // through the proxy). Cart endpoints work because /cart/*.js is a
    // privileged path that uses cookie auth. Same code works on the real
    // staging origin. Surface a useful message so future devs don't burn
    // an afternoon diagnosing.
    if (response.status === 401) {
      const wwwAuth = response.headers.get('WWW-Authenticate') || '';
      if (wwwAuth.indexOf('Bearer') === 0) {
        throw new Error(
          '[Sections] fetchSection ' + sectionId + ' got 401 + Bearer challenge — ' +
          'Section Rendering API does not work through `shopify theme dev` (localhost ' +
          'proxy). Push to a preview theme (`npm run push:dev`) and test against the ' +
          'staging URL instead.'
        );
      }
    }
    throw new Error('[Sections] fetchSection ' + sectionId + ' failed: HTTP ' + response.status);
  }
  const contentType = response.headers.get('Content-Type') || '';
  if (contentType.indexOf('application/json') < 0) {
    throw new Error(
      '[Sections] fetchSection ' + sectionId + ' got non-JSON response (' + contentType +
      ') — URL may have redirected (password page? auth challenge?)'
    );
  }
  let json;
  try {
    json = await response.json();
  } catch (e) {
    throw new Error('[Sections] fetchSection ' + sectionId + ' JSON parse failed: ' + e.message);
  }
  const html = json[sectionId];
  if (typeof html !== 'string') {
    throw new Error(
      '[Sections] fetchSection ' + sectionId + ' missing from response — section not registered ' +
      'for the resolved template at ' + base
    );
  }
  log('[Sections] fetched ' + sectionId + ' (' + html.length + ' bytes)');
  return html;
}

log('[Sections] loaded');
